import type { AppBoleta, AppBoletaProducto } from '../../types/empacadora';

function fmtFLong(s?: string | null) {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function fmtArea(n?: number | null): string {
  if (n == null) return '';
  return Number(n).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function maxReingreso(prods: AppBoletaProducto[]): string {
  const vals = prods
    .filter(p => !p.es_agua && !p.excluida && p.reingreso && p.reingreso !== 'N/A')
    .map(p => p.reingreso!);
  if (vals.length === 0) return 'N/A';
  vals.sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, '') || '0', 10);
    const nb = parseInt(b.replace(/\D/g, '') || '0', 10);
    return nb - na;
  });
  return vals[0];
}

const BANDERA_HTML = `<div style="display:inline-flex;gap:2px;align-items:center;">
  <div style="width:13px;height:13px;background:#e53935;border:1px solid #999;"></div>
  <div style="width:13px;height:13px;background:#fdd835;border:1px solid #999;"></div>
  <div style="width:13px;height:13px;background:#43a047;border:1px solid #999;"></div>
</div>`;

export async function imprimirReingreso(
  boletas: AppBoleta[],
  getProductos: (id: number) => Promise<AppBoletaProducto[]>,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
) {
  if (boletas.length === 0) return;

  const allProds = await Promise.all(boletas.map(b => getProductos(b.id)));
  const reingresoMap = new Map<number, string>();
  boletas.forEach((b, i) => { reingresoMap.set(b.id, maxReingreso(allProds[i])); });

  // Agrupar por semana (plan), manteniendo orden de aparición
  const semanasOrden: string[] = [];
  const porSemana = new Map<string, AppBoleta[]>();
  for (const b of boletas) {
    const key = b.semana ?? 'Sin semana';
    if (!porSemana.has(key)) { porSemana.set(key, []); semanasOrden.push(key); }
    porSemana.get(key)!.push(b);
  }

  const today = new Date().toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });

  const logoHtml = empresaLogo
    ? `<img src="${empresaLogo}" alt="Logo" style="width:58px;height:58px;object-fit:contain;">`
    : `<div style="width:58px;height:58px;border:1px dashed #bbb;display:flex;align-items:center;justify-content:center;font-size:6pt;color:#aaa;text-align:center;">LOGO</div>`;

  function fmtSemana(s: string): string {
    // "19-2026" → "19-26"
    const [w, y] = s.split('-');
    if (w && y && y.length === 4) return `${w}-${y.slice(2)}`;
    return s;
  }

  function buildHeader(semana: string) {
    return `
<div class="page-header">
  <table class="hdr-table">
    <tr>
      <td class="hdr-logo">${logoHtml}</td>
      <td class="hdr-center">
        <div class="empresa">${empresaNombre}</div>
        <div class="sgap">Sistema de Gestión Ambiental Global G.A.P.</div>
        <div class="titulo">RE-08 &nbsp;&nbsp; CONTROL DE REINGRESOS A LOTES</div>
        <div class="firmantes">
          <span>Elaborado por: Lorna Gómez Araya</span>
          <span>Aprobado por: Yansy Vásquez Zúñiga</span>
        </div>
      </td>
      <td class="hdr-right">
        <div class="sgap-ref">S.G.G.A.P</div>
        <div>Ref. SGA: CB8.6.1-8.62</div>
        <div class="semana-val">Semana: <strong>${fmtSemana(semana)}</strong></div>
        <div>Impreso el: ${today}</div>
      </td>
    </tr>
  </table>
</div>`;
  }

  // Un bloque por semana, con su propio header y tablas por etapa dentro
  const bloques = semanasOrden.map((semana, sIdx) => {
    const boletasSemana = porSemana.get(semana)!;

    // Sub-agrupar por etapa dentro de la semana
    const etapasOrden: string[] = [];
    const porEtapa = new Map<string, AppBoleta[]>();
    for (const b of boletasSemana) {
      const key = b.etapa_nombre ?? 'Sin etapa';
      if (!porEtapa.has(key)) { porEtapa.set(key, []); etapasOrden.push(key); }
      porEtapa.get(key)!.push(b);
    }

    const tablas = etapasOrden.map(etapa => {
      const lista = porEtapa.get(etapa)!.sort((a, b) =>
        (a.lote_num ?? '').localeCompare(b.lote_num ?? '', undefined, { numeric: true })
      );

      const filas = lista.map(b => `
        <tr>
          <td class="center fecha-aplica">${b.fecha_aplicacion ? fmtFLong(b.fecha_aplicacion) : ''}</td>
          <td class="center">${b.grupo_forza ?? ''}</td>
          <td class="center bold">${b.lote_num ?? ''}</td>
          <td class="left bloques">${b.bloques ?? ''}</td>
          <td class="left ciclo">${b.ciclo_nombre ?? ''}</td>
          <td class="right area">${fmtArea(b.area_ha)}</td>
          <td class="center mono">${b.numero ? String(b.numero).replace(/^0+/, '') : ''}</td>
          <td class="center reingreso">${reingresoMap.get(b.id) ?? ''}</td>
          <td class="center bandera">${BANDERA_HTML}</td>
          <td class="center recol"></td>
          <td class="center firma"></td>
        </tr>`).join('');

      return `<div class="bloque-etapa">
        <table class="re08">
          <thead>
            <tr class="etapa-hdr"><td colspan="11">${etapa}</td></tr>
            <tr>
              <th>Fecha Real<br>de Aplicación</th>
              <th>Grupo<br>Forza</th>
              <th>Lote</th>
              <th>Bloque / Sección</th>
              <th>Ciclo</th>
              <th>Área<br>(ha)</th>
              <th>No. Boleta</th>
              <th>Período<br>Reingreso</th>
              <th>Color /<br>Bandera</th>
              <th>Fecha Recolección<br>de Bandera</th>
              <th>Firma<br>Responsable</th>
            </tr>
          </thead>
          <tbody>${filas}</tbody>
        </table>
      </div>`;
    }).join('');

    return `<div class="bloque-plan${sIdx > 0 ? ' page-break' : ''}">
      ${buildHeader(semana)}
      ${tablas}
      <div class="footer">
        <span>Programa: Control de Reingresos a Lotes — RE-08</span>
        <span>Cédula Jurídica: ${empresaCedula}</span>
      </div>
    </div>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>RE-08 Control de Reingresos</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 8.5pt; color: #000; background: #fff; }

  @page {
    size: Letter landscape;
    margin: 6mm 8mm 8mm 8mm;
  }

  /* ── Bloque por plan (semana) ── */
  .bloque-plan { margin-bottom: 0; }
  .page-break { break-before: page; page-break-before: always; }

  /* ── Encabezado por plan ── */
  .page-header {
    margin-bottom: 5px;
  }
  .hdr-table {
    width: 100%;
    border-collapse: collapse;
    border: 1.5px solid #000;
    margin-bottom: 3px;
  }
  .hdr-logo {
    width: 72px;
    border-right: 1px solid #000;
    text-align: center;
    vertical-align: middle;
    padding: 4px;
  }
  .hdr-center {
    text-align: center;
    padding: 5px 10px;
    border-right: 1px solid #000;
    vertical-align: middle;
  }
  .hdr-center .empresa {
    font-size: 14pt;
    font-weight: bold;
    color: #003399;
    text-decoration: underline;
    letter-spacing: .3px;
  }
  .hdr-center .sgap {
    font-size: 8.5pt;
    font-style: italic;
    color: #003399;
    font-weight: bold;
    margin-top: 2px;
  }
  .hdr-center .titulo {
    font-size: 11pt;
    font-weight: bold;
    margin-top: 5px;
    letter-spacing: .2px;
  }
  .hdr-center .firmantes {
    display: flex;
    justify-content: space-around;
    font-size: 7pt;
    color: #333;
    margin-top: 5px;
  }
  .hdr-right {
    width: 170px;
    vertical-align: middle;
    padding: 5px 7px;
    font-size: 7pt;
    line-height: 1.75;
  }
  .hdr-right .sgap-ref { font-weight: bold; font-size: 8pt; }
  .hdr-right .semana-val { font-size: 10pt; font-weight: bold; margin-top: 2px; }
  .meta-impreso { font-size: 7.5pt; color: #444; text-align: right; margin-bottom: 4px; }

  /* ── Bloques por etapa ── */
  .bloque-etapa { margin-bottom: 10px; }
  .bloque-etapa + .bloque-etapa { break-before: page; page-break-before: always; }

  /* ── Tabla RE-08 ── */
  table.re08 { width: 100%; border-collapse: collapse; font-size: 10pt; }

  tr.etapa-hdr td {
    background: #1a3a6c;
    color: #fff;
    font-weight: bold;
    font-size: 12pt;
    text-align: center;
    padding: 5px 8px;
    border: 1px solid #1a3a6c;
    letter-spacing: .3px;
  }
  table.re08 thead tr:not(.etapa-hdr) th {
    background: #c8d8f0;
    border: 1px solid #888;
    padding: 4px 5px;
    text-align: center;
    font-size: 9pt;
    vertical-align: bottom;
    line-height: 1.3;
  }
  table.re08 td {
    border: 1px solid #bbb;
    padding: 4px 5px;
    vertical-align: middle;
  }
  tbody tr:nth-child(even) { background: #f0f4fa; }

  td.center   { text-align: center; }
  td.right    { text-align: right; }
  td.left     { text-align: left; }
  td.bold     { font-weight: bold; }
  td.mono     { font-family: 'Courier New', monospace; font-size: 9.5pt; font-weight: bold; }
  td.bloques  { font-size: 9pt; word-break: break-word; max-width: 180px; }
  td.ciclo    { font-size: 9pt; word-break: break-word; max-width: 150px; }
  td.area     { text-align: right; font-variant-numeric: tabular-nums; min-width: 46px; }
  td.bandera  { min-width: 50px; padding: 2px 4px; }
  td.recol    { min-width: 68px; }
  td.firma    { min-width: 75px; }
  td.reingreso { min-width: 52px; }
  td.fecha-aplica { min-width: 62px; }

  /* ── Footer ── */
  .footer {
    margin-top: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 7pt;
    color: #555;
    border-top: 1px solid #ccc;
    padding-top: 3px;
  }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tr.etapa-hdr td,
    tbody tr:nth-child(even),
    table.re08 thead tr:not(.etapa-hdr) th,
    .hdr-table { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    .page-header { display: block; }
    .bloque-etapa { break-inside: avoid-page; page-break-inside: avoid; }
    .bloque-etapa + .bloque-etapa { break-before: page; page-break-before: always; }
    .bloque-etapa table.re08 thead { display: table-header-group; }
  }
</style>
</head>
<body>

${bloques}

</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    if (iframe.contentDocument) iframe.contentDocument.title = 'RE-08 Control de Reingresos';
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 600);
}
